import { Controller } from "@hotwired/stimulus"

// 検索動線の地図表示。並び順は保存しないため、ドラッグはDOM順を入れ替えるだけにして
// サーバへのリクエストは一切送らない。印刷時だけ現在のDOM順を hidden field に書き戻す。
export default class extends Controller<HTMLElement> {
  static targets = ["item", "list", "fields"]

  declare readonly itemTargets: HTMLElement[]
  declare readonly listTarget: HTMLElement
  declare readonly fieldsTarget: HTMLElement

  private draggedItem?: HTMLElement
  private dropTarget?: HTMLElement
  private dropBefore = true

  dragStart(event: DragEvent) {
    const item = event.currentTarget as HTMLElement
    this.draggedItem = item
    item.classList.add("opacity-40")
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", item.dataset.vacancyId || "")
    }
  }

  dragOver(event: DragEvent) {
    event.preventDefault()
    const target = event.currentTarget as HTMLElement
    if (!this.draggedItem || target === this.draggedItem) {
      this.clearDropHighlight()
      return
    }

    this.clearDropHighlight()
    this.dropTarget = target
    const bounds = target.getBoundingClientRect()
    this.dropBefore = event.clientY < bounds.top + bounds.height / 2
    target.style.border = "1.5px dashed #3C50E0"
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"
  }

  dragLeave(event: DragEvent) {
    const item = event.currentTarget as HTMLElement
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return
    if (event.currentTarget === this.dropTarget) this.clearDropHighlight()
  }

  drop(event: DragEvent) {
    event.preventDefault()
    const draggedItem = this.draggedItem
    const target = this.dropTarget || (event.currentTarget as HTMLElement)
    const before = this.dropTarget ? this.dropBefore : true
    if (!draggedItem || target === draggedItem) {
      this.cleanupDrag()
      return
    }

    this.listTarget.insertBefore(draggedItem, before ? target : target.nextSibling)
    this.cleanupDrag()
    this.updateMapNumbers()
  }

  dragEnd() {
    this.cleanupDrag()
  }

  focus(event: MouseEvent) {
    const item = event.currentTarget as HTMLElement
    if (item.dataset.mappable !== "true") return

    window.dispatchEvent(new CustomEvent("property-map:focus", {
      detail: { id: Number(item.dataset.vacancyId) },
    }))
  }

  back() {
    window.history.back()
  }

  // カードは form の外にあるため、送信直前に現在のDOM順で hidden を作り直す。
  // 並び順（vacancy_ids[]）と任意入力（notes[<id>]）を同じ場所で組み立てないと、
  // 片方だけ fields の入れ替えで消える。任意入力が空の物件は hidden を作らない。
  prepareSubmit() {
    const fields: HTMLInputElement[] = []
    this.itemTargets.forEach((item) => {
      const id = item.dataset.vacancyId || ""
      fields.push(this.hiddenField("vacancy_ids[]", id))
      const note = item.querySelector<HTMLInputElement>("[data-vacancy-note]")?.value.trim()
      if (note) fields.push(this.hiddenField(`notes[${id}]`, note))
    })
    this.fieldsTarget.replaceChildren(...fields)
  }

  // 入力欄の上でドラッグを始めるとブラウザがカードのドラッグを優先し、文字を選択できない。
  // 入力中だけカードの draggable を外し、フォーカスが外れたら戻す。
  suspendDrag(event: Event) {
    const item = this.itemFor(event)
    if (item) item.draggable = false
  }

  resumeDrag(event: Event) {
    const item = this.itemFor(event)
    if (item) item.draggable = true
  }

  private itemFor(event: Event) {
    const element = event.currentTarget as HTMLElement
    return element.closest<HTMLElement>("[data-vacancy-map-order-target='item']")
  }

  private hiddenField(name: string, value: string) {
    const field = document.createElement("input")
    field.type = "hidden"
    field.name = name
    field.value = value
    return field
  }

  private updateMapNumbers() {
    const labels: Record<string, string> = {}
    let number = 0
    this.itemTargets.forEach((item) => {
      const badge = item.querySelector<HTMLElement>("[data-vacancy-map-number]")
      if (item.dataset.mappable === "true") {
        number += 1
        const label = String(number)
        if (badge) badge.textContent = label
        labels[item.dataset.vacancyId || ""] = label
      } else if (badge) {
        badge.remove()
      }
    })
    window.dispatchEvent(new CustomEvent("property-map:labels-changed", { detail: { labels } }))
  }

  private cleanupDrag() {
    this.draggedItem?.classList.remove("opacity-40")
    this.draggedItem = undefined
    this.clearDropHighlight()
  }

  private clearDropHighlight() {
    if (this.dropTarget) this.dropTarget.style.border = ""
    this.dropTarget = undefined
  }
}
